import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

const initialState = {
  cartItems: [],
  isLoading: false,
  total: 0,
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItem = state.cartItems.find(
        item => item.product.id === action.payload.product.id
      );

      let newCartItems;
      if (existingItem) {
        newCartItems = state.cartItems.map(item =>
          item.product.id === action.payload.product.id
            ? { ...item, quantity: item.quantity + action.payload.quantity }
            : item
        );
      } else {
        newCartItems = [...state.cartItems, action.payload];
      }

      const newTotal = calculateTotal(newCartItems);
      
      return {
        ...state,
        cartItems: newCartItems,
        total: newTotal,
      };
    }

    case 'REMOVE_FROM_CART': {
      const newCartItems = state.cartItems.filter(
        item => item.product.id !== action.payload
      );
      const newTotal = calculateTotal(newCartItems);
      
      return {
        ...state,
        cartItems: newCartItems,
        total: newTotal,
      };
    }

    case 'UPDATE_QUANTITY': {
      const newCartItems = state.cartItems.map(item =>
        item.product.id === action.payload.productId
          ? { ...item, quantity: action.payload.quantity }
          : item
      ).filter(item => item.quantity > 0);

      const newTotal = calculateTotal(newCartItems);
      
      return {
        ...state,
        cartItems: newCartItems,
        total: newTotal,
      };
    }

    case 'CLEAR_CART':
      return {
        ...state,
        cartItems: [],
        total: 0,
      };

    case 'LOAD_CART':
      return {
        ...state,
        cartItems: action.payload.cartItems || [],
        total: action.payload.total || 0,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
};

// حساب المجموع الكلي
const calculateTotal = (cartItems) => {
  return cartItems.reduce((total, item) => {
    return total + (item.product.price * item.quantity);
  }, 0);
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // تحميل السلة من localStorage عند بدء التطبيق
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        dispatch({
          type: 'LOAD_CART',
          payload: cartData,
        });
      } catch (error) {
        console.error('خطأ في تحميل السلة:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // حفظ السلة في localStorage عند تغييرها
  useEffect(() => {
    if (state.cartItems.length > 0 || state.total > 0) {
      localStorage.setItem('cart', JSON.stringify({
        cartItems: state.cartItems,
        total: state.total,
      }));
    } else {
      localStorage.removeItem('cart');
    }
  }, [state.cartItems, state.total]);

  // إضافة منتج للسلة
  const addToCart = (product, quantity = 1, variant = null) => {
    // التحقق من توفر المنتج
    if (product.status !== 'active') {
      toast.error('هذا المنتج غير متوفر حالياً');
      return;
    }

    // التحقق من الكمية المتوفرة
    if (product.track_quantity && product.quantity < quantity) {
      toast.error('الكمية المطلوبة غير متوفرة');
      return;
    }

    const cartItem = {
      product,
      quantity,
      variant,
      addedAt: new Date().toISOString(),
    };

    dispatch({
      type: 'ADD_TO_CART',
      payload: cartItem,
    });

    toast.success('تم إضافة المنتج للسلة');
  };

  // إزالة منتج من السلة
  const removeFromCart = (productId) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: productId,
    });

    toast.success('تم إزالة المنتج من السلة');
  };

  // تحديث كمية منتج في السلة
  const updateQuantity = (productId, quantity) => {
    if (quantity < 0) return;

    if (quantity === 0) {
      removeFromCart(productId);
      return;
    }

    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { productId, quantity },
    });
  };

  // مسح السلة
  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast.success('تم مسح السلة');
  };

  // الحصول على عدد العناصر في السلة
  const getCartItemsCount = () => {
    return state.cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // التحقق من وجود منتج في السلة
  const isInCart = (productId) => {
    return state.cartItems.some(item => item.product.id === productId);
  };

  // الحصول على كمية منتج في السلة
  const getItemQuantity = (productId) => {
    const item = state.cartItems.find(item => item.product.id === productId);
    return item ? item.quantity : 0;
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItemsCount,
    isInCart,
    getItemQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};